/**
 * useTextureScripts — manages the lifecycle of JS-driven live textures and
 * static SVG textures.
 *
 * For each ColoredGeometry that has textureScripts entries, this hook handles
 * two texture source types:
 *
 * ── type: "script" (JS canvas workers) ──────────────────────────────────────
 *  1. Looks up the JS source code from the provided project files.
 *  2. Parses optional `export const width/height/fps` declarations.
 *  3. Spawns a sandboxed TextureWorker for each unique (filename, channel) pair.
 *  4. Creates a THREE.CanvasTexture backed by an OffscreenCanvas.
 *  5. On each FRAME message from the worker, applies the ImageBitmap to the
 *     texture and calls R3F's invalidate() to trigger a render.
 *  6. Cleans up workers and textures on recompile or unmount.
 *
 * ── type: "svg" (static SVG rasterization) ──────────────────────────────────
 *  1. Looks up the SVG source from the provided project files.
 *  2. Determines raster size: explicit width/height from the SCAD call, or
 *     natural dimensions inferred from the SVG viewBox/width/height attributes,
 *     falling back to DEFAULT_TEXTURE_WIDTH × DEFAULT_TEXTURE_HEIGHT.
 *  3. Rasterizes via createImageBitmap() — no worker, one async microtask.
 *  4. Draws the bitmap onto an OffscreenCanvas → THREE.CanvasTexture.
 *  5. Registers the texture in textureMapRef and calls onTextureRegistered().
 *  6. Hot-reloads when the SVG content changes.
 *  SVG textures are always static (they never put the frameloop into "always"
 *  mode — they call invalidate() once after rasterization, then are done).
 *
 * ── Inter-script communication ───────────────────────────────────────────────
 * Workers in the same model can exchange named values via __publish__ /
 * __subscribe__. The main thread acts as the relay: it maintains a sharedState
 * cache of the last published value per key, seeds new workers with that cache
 * on START, and forwards PUBLISH messages to all other running workers as DATA
 * messages. The publishing worker does not receive its own message back.
 *
 * ── Shared behaviour ─────────────────────────────────────────────────────────
 * Workers/textures are deduplicated by (filename, channel) — multiple
 * geometries that reference the same file+channel share one worker/texture.
 *
 * The textureMapRef is a nested map: uuid → channel → THREE.Texture.
 * This allows each geometry to have multiple textures for different PBR
 * channels (e.g. "color", "roughness", "metalness", "normal", etc.).
 *
 * Supported channels:
 *   "color"     — diffuse map (sRGB, default)
 *   "roughness" — roughnessMap (linear)
 *   "metalness" — metalnessMap (linear)
 *   "normal"    — normalMap (linear)
 *   "alpha"     — alphaMap (linear)
 *   "emissive"  — emissiveMap (sRGB)
 *   "ao"        — aoMap (linear)
 *
 * FPS modes (JS scripts only):
 *   undefined  — uncapped; script drives its own rAF loop at full speed
 *   0          — static; only the first frame is sent; PARAMS messages
 *                trigger one extra frame so param-driven textures still update
 *   1..N       — throttled to N frames per second
 *
 * Must be called inside the R3F <Canvas> so useThree() is available.
 *
 * Design note: textures are applied directly to materials in the FRAME handler
 * via `material.needsUpdate = true`, then R3F's `invalidate()` wakes the render
 * loop. There is NO React state update for individual frames — this avoids
 * triggering a React re-render cascade on every frame.
 */

import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import type { ColoredGeometry } from '@/components/viewer/types';
import type { ProjectFile } from '@/components/files/FileManager';
import type { Parameter } from '@shared/types';
import {
  parseScriptMetadata,
  DEFAULT_TEXTURE_WIDTH,
  DEFAULT_TEXTURE_HEIGHT,
} from '@/utils/textureUtils';
import { usePerformanceStore } from '@/stores/performanceStore';

/**
 * Valid texture channel names. Each maps to a specific Three.js material map slot
 * with the appropriate color space for that kind of data.
 */
export type TextureChannel =
  | 'color'
  | 'roughness'
  | 'metalness'
  | 'normal'
  | 'alpha'
  | 'emissive'
  | 'ao';

const VALID_CHANNELS = new Set<string>([
  'color',
  'roughness',
  'metalness',
  'normal',
  'alpha',
  'emissive',
  'ao',
]);

// ---------------------------------------------------------------------------
// Static image rasterization helpers (SVG, PNG, JPG, WebP, …)
// ---------------------------------------------------------------------------

/**
 * Derive the MIME type for a file from its extension.
 * Used to build the correct Blob for createImageBitmap().
 */
function mimeTypeForFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'svg':
      return 'image/svg+xml';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    case 'png':
    default:
      return 'image/png';
  }
}

/**
 * Parse the natural pixel dimensions of an SVG document.
 *
 * Resolution order:
 *   1. Explicit `width` / `height` attributes with pixel values
 *   2. `viewBox` attribute (w × h fields)
 *   3. Fallback: DEFAULT_TEXTURE_WIDTH × DEFAULT_TEXTURE_HEIGHT
 *
 * For non-SVG images (PNG/JPG/WebP) this is not needed — createImageBitmap()
 * decodes native dimensions automatically.
 */
function parseSvgNaturalSize(svgContent: string): {
  width: number;
  height: number;
} {
  // Try width/height attributes (e.g. width="512" or width="512px")
  const wMatch = svgContent.match(/<svg[^>]*\swidth=["']([0-9.]+)(?:px)?["']/i);
  const hMatch = svgContent.match(
    /<svg[^>]*\sheight=["']([0-9.]+)(?:px)?["']/i,
  );
  if (wMatch && hMatch) {
    const w = parseFloat(wMatch[1]);
    const h = parseFloat(hMatch[1]);
    if (w > 0 && h > 0) return { width: w, height: h };
  }

  // Fall back to viewBox="minX minY width height"
  const vbMatch = svgContent.match(/<svg[^>]*\sviewBox=["']([0-9.\s-]+)["']/i);
  if (vbMatch) {
    const parts = vbMatch[1].trim().split(/\s+/);
    if (parts.length === 4) {
      const w = parseFloat(parts[2]);
      const h = parseFloat(parts[3]);
      if (w > 0 && h > 0) return { width: w, height: h };
    }
  }

  return { width: DEFAULT_TEXTURE_WIDTH, height: DEFAULT_TEXTURE_HEIGHT };
}

/**
 * Rasterize any image source to an ImageBitmap.
 *
 * SVG: uses Image + OffscreenCanvas (createImageBitmap with SVG blobs is
 *      unreliable in Chrome). width/height default to the SVG's naturalWidth/
 *      naturalHeight; pass explicit values to override.
 *
 * PNG/JPG/WebP: uses createImageBitmap() directly. width/height are optional
 *               resize hints; omitting them preserves native resolution.
 */
export async function rasterizeBitmap(
  content: string | ArrayBuffer,
  filename: string,
  width?: number,
  height?: number,
): Promise<ImageBitmap> {
  const mime = mimeTypeForFilename(filename);

  if (mime === 'image/svg+xml') {
    // Chrome does not support createImageBitmap() with SVG blobs reliably.
    // Use the Image + OffscreenCanvas path instead, which works everywhere.
    return new Promise<ImageBitmap>((resolve, reject) => {
      const svgContent =
        typeof content === 'string'
          ? content
          : new TextDecoder().decode(content);
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const w = width ?? (img.naturalWidth || DEFAULT_TEXTURE_WIDTH);
        const h = height ?? (img.naturalHeight || DEFAULT_TEXTURE_HEIGHT);
        const canvas = new OffscreenCanvas(w, h);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get 2d context for SVG rasterization'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.transferToImageBitmap());
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(`Failed to load SVG image: ${filename}`));
      };
      img.src = url;
    });
  }

  // For raster formats (PNG, JPG, WebP) createImageBitmap works reliably.
  const blob = new Blob([content], { type: mime });
  const opts: ImageBitmapOptions = { resizeQuality: 'high' };
  if (width !== undefined) opts.resizeWidth = width;
  if (height !== undefined) opts.resizeHeight = height;
  return createImageBitmap(blob, opts);
}

// ---------------------------------------------------------------------------
// Color-space helpers
// ---------------------------------------------------------------------------

/**
 * Returns the appropriate Three.js color space for a given channel.
 * Color and emissive channels are gamma-encoded (sRGB); data channels are linear.
 */
function colorSpaceForChannel(channel: TextureChannel): THREE.ColorSpace {
  return channel === 'color' || channel === 'emissive'
    ? THREE.SRGBColorSpace
    : THREE.NoColorSpace;
}

/**
 * Convert a Parameter[] array into a plain { name: value } record suitable
 * for sending to a texture worker.  Only serializable primitive / array
 * values are included so the object is safe for structured-clone transfer.
 */
function parametersToRecord(parameters: Parameter[]): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (const p of parameters) {
    record[p.name] = p.value;
  }
  return record;
}

/** A live texture entry managed for a single (filename, channel) pair */
interface TextureEntry {
  /** "script" = JS canvas worker, "svg" = static SVG rasterization */
  type: 'script' | 'image';
  /** Only set for script entries */
  worker?: Worker;
  texture: THREE.CanvasTexture;
  channel: TextureChannel;
  /**
   * The declared fps from the script (undefined = uncapped, 0 = static, >0 = throttled).
   * SVG entries always behave like fps=0 (static) — this field is undefined for them.
   * Animated = fps !== 0 (undefined or >0).
   */
  fps: number | undefined;
  /** Geometry UUIDs that reference this entry */
  refCount: number;
  /** The source content that was used to create this entry (JS code or SVG markup) */
  scriptCode: string;
}

/**
 * Manages live JS texture workers and populates the provided textureMapRef.
 *
 * The caller creates and owns textureMapRef. This hook writes into it as
 * workers start and frames arrive. There are no React state updates for
 * individual frames — Three.js materials are updated directly and R3F's
 * invalidate() is called to wake the render loop.
 *
 * @param onHasAnimatedScripts Called whenever the set of running animated
 *   (non-static) workers changes. Pass this to the R3F Canvas frameloop
 *   control so it only switches to "always" mode when actually needed.
 */
export function useTextureScripts(
  coloredGeometries: ColoredGeometry[],
  projectFiles: ProjectFile[],
  /** Outer key: geometry UUID. Inner key: channel name. */
  textureMapRef: React.MutableRefObject<
    Map<string, Map<string, THREE.Texture>>
  >,
  onTextureRegistered?: () => void,
  parameters: Parameter[] = [],
  onHasAnimatedScripts?: (hasAnimated: boolean) => void,
  /**
   * Optional callback that returns additional workers (e.g. shader workers)
   * that should also receive pub/sub DATA messages when a texture worker
   * publishes a value. Enables cross-system pub/sub between texture and shader scripts.
   */
  getExtraWorkers?: () => Worker[],
): {
  publish: (key: string, value: unknown) => void;
  subscribe: (key: string, callback: (value: unknown) => void) => () => void;
  sharedStateRef: React.MutableRefObject<Map<string, unknown>>;
} {
  const { invalidate } = useThree();
  // Stable refs — avoids effect re-runs when R3F recreates invalidate or
  // parent recreates the callbacks
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;
  const onTextureRegisteredRef = useRef(onTextureRegistered);
  onTextureRegisteredRef.current = onTextureRegistered;
  const onHasAnimatedScriptsRef = useRef(onHasAnimatedScripts);
  onHasAnimatedScriptsRef.current = onHasAnimatedScripts;

  const setTextureWorkerStats = usePerformanceStore(
    (state) => state.setTextureWorkerStats,
  );
  const pushWorkerSample = usePerformanceStore(
    (state) => state.pushWorkerSample,
  );
  const compileWorkers = usePerformanceStore((state) => state.compileWorkers);
  const compileWorkersRef = useRef(compileWorkers);
  compileWorkersRef.current = compileWorkers;

  /**
   * Map from `${filename}@${channel}` → TextureEntry.
   * Kept as a ref so workers survive React re-renders without being restarted.
   */
  const entriesRef = useRef<Map<string, TextureEntry>>(new Map());

  /**
   * Map from geometry UUID → Set of entry keys (`${filename}@${channel}`)
   * that this UUID references. Used for cleanup bookkeeping.
   */
  const uuidToEntryKeysRef = useRef<Map<string, Set<string>>>(new Map());

  /**
   * Shared-state cache for inter-script pub/sub.
   * Stores the last value published under each key by any worker in this model.
   * New workers receive this cache on START so that __subscribe__ callbacks fire
   * immediately with the latest known value even if the publisher ran earlier.
   * Cleared when all workers are torn down (unmount or full recompile).
   */
  const sharedStateRef = useRef<Map<string, unknown>>(new Map());

  /**
   * External subscribers (e.g. ParameterSection) that want to be notified when
   * a value is published under a specific key.
   * Map from key → Set of callback functions.
   */
  const subscribersRef = useRef<Map<string, Set<(value: unknown) => void>>>(
    new Map(),
  );

  // Keep a stable ref to the latest parameters so the spawnWorker callback
  // (which has no deps) can read the current values.
  const parametersRef = useRef(parameters);
  parametersRef.current = parameters;

  /**
   * Recompute and broadcast whether any running workers are animated.
   * Called after spawning or terminating workers.
   */
  const broadcastAnimationState = useCallback(() => {
    const hasAnimated = [...entriesRef.current.values()].some(
      (e) => e.fps !== 0,
    );
    onHasAnimatedScriptsRef.current?.(hasAnimated);
  }, []);

  /**
   * Push the current texture worker count to the performance store.
   * All texture workers are considered "active" (they produce frames while running).
   * Stored in a ref so it can be called from stable callbacks without being a dep.
   */
  const setTextureWorkerStatsRef = useRef(setTextureWorkerStats);
  setTextureWorkerStatsRef.current = setTextureWorkerStats;
  const pushWorkerSampleRef = useRef(pushWorkerSample);
  pushWorkerSampleRef.current = pushWorkerSample;

  const broadcastTextureWorkerStats = useCallback(() => {
    const count = entriesRef.current.size;
    setTextureWorkerStatsRef.current({ active: count, total: count });
    // Record a combined worker sample for the history graph
    const cw = compileWorkersRef.current;
    pushWorkerSampleRef.current({
      t: Date.now(),
      active: cw.active + count,
      total: cw.total + count,
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Helper: create and configure a CanvasTexture placeholder
  // ---------------------------------------------------------------------------

  /**
   * Creates a THREE.CanvasTexture backed by a blank OffscreenCanvas.
   * Shared between JS-script and SVG texture paths.
   */
  const createPlaceholderTexture = useCallback(
    (width: number, height: number, channel: TextureChannel) => {
      const placeholderCanvas = new OffscreenCanvas(width, height);
      const texture = new THREE.CanvasTexture(
        placeholderCanvas as unknown as HTMLCanvasElement,
      );
      texture.colorSpace = colorSpaceForChannel(channel);
      texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      // ImageBitmap from transferToImageBitmap() / createImageBitmap() is
      // already in GPU-ready (Y-flipped) orientation. CanvasTexture defaults
      // to flipY=true which would flip it a second time — disable it.
      texture.flipY = false;
      return texture;
    },
    [],
  );

  /**
   * Register a texture in textureMapRef for all UUIDs that reference this
   * entryKey. Notifies ModelRenderer once so it picks up the new texture.
   */
  const registerTexture = useCallback(
    (
      entryKey: string,
      channel: TextureChannel,
      filename: string,
      texture: THREE.CanvasTexture,
      neededUuids: Map<string, Set<string>>,
    ) => {
      for (const [uuid, entryKeys] of neededUuids) {
        if (entryKeys.has(entryKey)) {
          let channelMap = textureMapRef.current.get(uuid);
          if (!channelMap) {
            channelMap = new Map();
            textureMapRef.current.set(uuid, channelMap);
          }
          channelMap.set(channel, texture);
          console.log(
            `[TextureScripts] Mapped uuid ${uuid} channel "${channel}" → "${filename}"`,
          );
        }
      }
      onTextureRegisteredRef.current?.();
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // JS-script worker spawner
  // ---------------------------------------------------------------------------

  // Stable ref for spawnWorker so it doesn't need to be in the effect deps
  const spawnWorker = useCallback(
    (
      entryKey: string, // `${filename}@${channel}` or `${filename}@${channel}@${propsKey}`
      filename: string,
      channel: TextureChannel,
      scriptCode: string,
      fps: number | undefined,
      /** Map of uuid → Set<entryKey> for all geometries in this compile */
      neededUuids: Map<string, Set<string>>,
      /** Per-instance props from the SCAD third argument — exposed as `props` in the script */
      props?: Record<string, unknown>,
    ) => {
      const { width, height } = parseScriptMetadata(scriptCode);

      const fpsLabel =
        fps === undefined ? 'uncapped' : fps === 0 ? 'static' : `${fps} fps`;
      console.log(
        `[TextureScripts] Spawning worker for "${filename}" channel="${channel}" (${width}x${height}, ${fpsLabel})`,
      );

      const texture = createPlaceholderTexture(width, height, channel);

      // Register the texture in the UUID map so ModelRenderer can find it
      registerTexture(entryKey, channel, filename, texture, neededUuids);

      // Spawn the sandboxed worker
      const worker = new Worker(
        new URL('../../../worker/textureWorker.ts', import.meta.url),
        { type: 'module' },
      );

      worker.onmessage = (event: MessageEvent) => {
        const { type: msgType } = event.data as { type: string };

        if (msgType === 'FRAME') {
          const bitmap = event.data.bitmap as ImageBitmap;
          // Apply directly to the texture — no React state update needed.
          // Three.js will pick it up on the next R3F render frame.
          texture.image = bitmap;
          texture.needsUpdate = true;
          // Wake the demand-mode render loop
          invalidateRef.current();
          return;
        }

        if (msgType === 'READY') {
          console.log(
            `[TextureScripts] Worker READY for "${filename}" channel="${channel}"`,
          );
          return;
        }

        if (msgType === 'ERROR') {
          console.error(
            `[TextureScripts] Error in "${filename}" channel="${channel}":`,
            event.data.message,
          );
          return;
        }

        if (msgType === 'PUBLISH') {
          // A worker published a named value — cache it and relay to all others.
          const { key, value } = event.data as { key: string; value: unknown };
          sharedStateRef.current.set(key, value);
          // Fan out to every OTHER running script worker in this model
          for (const [otherKey, otherEntry] of entriesRef.current) {
            if (
              otherKey !== entryKey &&
              otherEntry.type === 'script' &&
              otherEntry.worker
            ) {
              otherEntry.worker.postMessage({ type: 'DATA', key, value });
            }
          }
          // Fan out to any extra workers (e.g. shader workers)
          if (getExtraWorkers) {
            for (const w of getExtraWorkers()) {
              w.postMessage({ type: 'DATA', key, value });
            }
          }
          // Notify external subscribers (e.g. ParameterSection @pubsub params)
          const subs = subscribersRef.current.get(key);
          if (subs) {
            for (const cb of subs) cb(value);
          }
          return;
        }
      };

      worker.onerror = (err) => {
        console.error(
          `[TextureScripts] Worker onerror for "${filename}" channel="${channel}":`,
          err,
        );
      };

      // Count how many UUIDs reference this specific entry key
      let refCount = 0;
      for (const entryKeys of neededUuids.values()) {
        if (entryKeys.has(entryKey)) refCount++;
      }

      const entry: TextureEntry = {
        type: 'script',
        worker,
        texture,
        channel,
        fps,
        refCount,
        scriptCode,
      };
      entriesRef.current.set(entryKey, entry);

      // Broadcast updated animation state (new worker may change the answer)
      broadcastAnimationState();

      // Broadcast updated texture worker count to the performance store
      broadcastTextureWorkerStats();

      // Send the script to the worker to start it (include fps + current params + instance props)
      const params = parametersToRecord(parametersRef.current);
      // Snapshot the shared-state cache so the new worker receives last-known
      // values for any keys that were already published by other workers
      const sharedStateSnapshot = Object.fromEntries(sharedStateRef.current);
      worker.postMessage({
        type: 'START',
        scriptCode,
        width,
        height,
        fps,
        params,
        ...(props && Object.keys(props).length > 0 ? { props } : {}),
        ...(Object.keys(sharedStateSnapshot).length > 0
          ? { sharedState: sharedStateSnapshot }
          : {}),
      });
    },
    [
      broadcastAnimationState,
      broadcastTextureWorkerStats,
      createPlaceholderTexture,
      registerTexture,
    ],
  );

  // ---------------------------------------------------------------------------
  // Static image loader (SVG/PNG/JPG/WebP — no worker, one async microtask)
  // ---------------------------------------------------------------------------

  const spawnStaticImage = useCallback(
    (
      entryKey: string,
      filename: string,
      channel: TextureChannel,
      /** String for SVG, ArrayBuffer for raster images (PNG/JPG/WebP) */
      content: string | ArrayBuffer,
      /** Explicit size from SCAD call; undefined = native / infer from SVG viewBox */
      explicitWidth: number | undefined,
      explicitHeight: number | undefined,
      neededUuids: Map<string, Set<string>>,
    ) => {
      // For SVG files, try to infer natural size from the markup when no
      // explicit size was given.  For raster images (PNG/JPG/WebP) native
      // dimensions are decoded by createImageBitmap() automatically when no
      // resize options are passed — so we only need a placeholder canvas size.
      const isSvg =
        typeof content === 'string' && filename.toLowerCase().endsWith('.svg');

      let placeholderWidth: number;
      let placeholderHeight: number;

      if (explicitWidth !== undefined && explicitHeight !== undefined) {
        placeholderWidth = explicitWidth;
        placeholderHeight = explicitHeight;
      } else if (isSvg && typeof content === 'string') {
        const natural = parseSvgNaturalSize(content);
        placeholderWidth = explicitWidth ?? natural.width;
        placeholderHeight = explicitHeight ?? natural.height;
      } else {
        // Raster image without explicit size — use defaults for the placeholder;
        // the real bitmap will arrive with native dimensions.
        placeholderWidth = DEFAULT_TEXTURE_WIDTH;
        placeholderHeight = DEFAULT_TEXTURE_HEIGHT;
      }

      console.log(
        `[TextureScripts] Loading image "${filename}" channel="${channel}" (placeholder ${placeholderWidth}x${placeholderHeight})`,
      );

      const texture = createPlaceholderTexture(
        placeholderWidth,
        placeholderHeight,
        channel,
      );

      // Register placeholder immediately so ModelRenderer picks it up even
      // before the async rasterization resolves.
      registerTexture(entryKey, channel, filename, texture, neededUuids);

      // Count how many UUIDs reference this specific entry key
      let refCount = 0;
      for (const entryKeys of neededUuids.values()) {
        if (entryKeys.has(entryKey)) refCount++;
      }

      // Use a string key for the entry scriptCode — for binary files, store a
      // sentinel so hot-reload detection still works (content identity check).
      const scriptCode =
        typeof content === 'string' ? content : `__binary__:${filename}`;

      const entry: TextureEntry = {
        type: 'image',
        texture,
        channel,
        fps: 0, // Static images never animate
        refCount,
        scriptCode,
      };
      entriesRef.current.set(entryKey, entry);

      // Image textures are static — no change to animation state
      broadcastAnimationState();
      broadcastTextureWorkerStats();

      // Rasterize/decode asynchronously — update texture when the bitmap is ready.
      // For SVGs, pass the resolved size (inferred from viewBox or explicit) so
      // createImageBitmap() has concrete pixel dimensions to rasterize to.
      // For raster images, pass explicit overrides only (undefined = native resolution).
      const rasterWidth = isSvg ? placeholderWidth : explicitWidth;
      const rasterHeight = isSvg ? placeholderHeight : explicitHeight;
      rasterizeBitmap(content, filename, rasterWidth, rasterHeight)
        .then((bitmap) => {
          // Guard: entry may have been removed while we were awaiting
          if (!entriesRef.current.has(entryKey)) return;
          texture.image = bitmap;
          texture.needsUpdate = true;
          invalidateRef.current();
          console.log(
            `[TextureScripts] Image "${filename}" decoded and uploaded`,
          );
        })
        .catch((err: unknown) => {
          console.error(
            `[TextureScripts] Image decode failed for "${filename}":`,
            err,
          );
        });
    },
    [
      broadcastAnimationState,
      broadcastTextureWorkerStats,
      createPlaceholderTexture,
      registerTexture,
    ],
  );

  // ---------------------------------------------------------------------------
  // Main effect — reconciles needed entries against running entries
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const entries = entriesRef.current;
    const uuidToEntryKeys = uuidToEntryKeysRef.current;
    const textureMap = textureMapRef.current;

    // Determine which (filename, channel, props) tuples are needed right now.
    // entryKey = `${filename}@${channel}` for prop-less calls, or
    //            `${filename}@${channel}@${stablePropsJson}` when props are present,
    //            so each unique prop set gets its own dedicated worker.
    const neededEntries = new Map<
      string,
      {
        filename: string;
        channel: TextureChannel;
        /** Raw file content — string for JS/SVG, ArrayBuffer for PNG/JPG/WebP */
        content: string | ArrayBuffer;
        /**
         * Identity key for hot-reload detection: for string content this is the
         * content itself; for binary content it is a stable sentinel string so
         * the identity check still works without serialising binary data.
         */
        scriptCode: string;
        type: 'script' | 'image';
        fps: number | undefined;
        /** Image only: explicit raster width from SCAD call */
        width?: number;
        /** Image only: explicit raster height from SCAD call */
        height?: number;
        /** Per-instance props from SCAD third argument — passed to the worker as `props` */
        props?: Record<string, unknown>;
      }
    >();
    // uuid → Set<entryKey>
    const neededUuids = new Map<string, Set<string>>();

    console.log(
      `[TextureScripts] Effect running. coloredGeometries: ${coloredGeometries.length}, projectFiles: ${projectFiles.length}`,
    );

    for (const cg of coloredGeometries) {
      if (!cg.textureScripts?.length) continue;

      for (const {
        filename,
        channel: rawChannel,
        type: entryType = 'script',
        width: explicitWidth,
        height: explicitHeight,
        props: instanceProps,
      } of cg.textureScripts) {
        // Validate channel, fall back to 'color' if unknown
        const channel: TextureChannel = VALID_CHANNELS.has(rawChannel)
          ? (rawChannel as TextureChannel)
          : 'color';

        if (channel !== rawChannel) {
          console.warn(
            `[TextureScripts] Unknown channel "${rawChannel}" for "${filename}", falling back to "color"`,
          );
        }

        // Include a stable props fingerprint in the key so that each unique
        // set of per-instance props spawns its own dedicated worker.
        const propsKey =
          instanceProps && Object.keys(instanceProps).length > 0
            ? `@${JSON.stringify(instanceProps, Object.keys(instanceProps).sort())}`
            : '';
        const entryKey = `${filename}@${channel}${propsKey}`;

        // Track uuid → entryKeys
        let entryKeys = neededUuids.get(cg.geometry.uuid);
        if (!entryKeys) {
          entryKeys = new Set();
          neededUuids.set(cg.geometry.uuid, entryKeys);
        }
        entryKeys.add(entryKey);

        console.log(
          `[TextureScripts] Geometry ${cg.geometry.uuid} needs "${filename}" channel="${channel}" (${entryType})`,
        );

        if (!neededEntries.has(entryKey)) {
          const file = projectFiles.find((f) => f.filename === filename);
          if (file) {
            if (entryType === 'image') {
              // Binary files (PNG/JPG/WebP) have ArrayBuffer content;
              // SVG files have string content.
              const scriptCode =
                typeof file.content === 'string'
                  ? file.content
                  : `__binary__:${filename}`;
              neededEntries.set(entryKey, {
                filename,
                channel,
                content: file.content,
                scriptCode,
                type: 'image',
                fps: 0,
                width: explicitWidth,
                height: explicitHeight,
              });
              console.log(
                `[TextureScripts] Found image source for "${filename}" (${typeof file.content === 'string' ? file.content.length + ' chars' : file.content.byteLength + ' bytes'})`,
              );
            } else if (typeof file.content === 'string') {
              const { fps } = parseScriptMetadata(file.content);
              neededEntries.set(entryKey, {
                filename,
                channel,
                content: file.content,
                scriptCode: file.content,
                type: 'script',
                fps,
                ...(instanceProps && Object.keys(instanceProps).length > 0
                  ? { props: instanceProps }
                  : {}),
              });
              console.log(
                `[TextureScripts] Found script source for "${filename}" (${file.content.length} chars, fps=${fps ?? 'uncapped'})`,
              );
            } else {
              console.warn(
                `[TextureScripts] Script file "${filename}" has unexpected binary content`,
              );
            }
          } else {
            console.warn(
              `[TextureScripts] File "${filename}" not found in projectFiles`,
              projectFiles.map((f) => f.filename),
            );
          }
        }
      }
    }

    // Remove UUID mappings and stop/dispose entries no longer needed
    for (const [uuid, oldEntryKeys] of uuidToEntryKeys) {
      if (!neededUuids.has(uuid)) {
        // This UUID is gone entirely — remove its channel map
        textureMap.delete(uuid);
      } else {
        // UUID still exists — remove only the channels it no longer uses
        const channelMap = textureMap.get(uuid);
        if (channelMap) {
          for (const oldKey of oldEntryKeys) {
            if (!neededUuids.get(uuid)?.has(oldKey)) {
              // Parse channel out of the entryKey
              const ch = oldKey.split('@')[1];
              channelMap.delete(ch);
            }
          }
        }
      }

      // Stop/dispose entries whose entryKeys are fully gone
      for (const oldKey of oldEntryKeys) {
        if (!neededEntries.has(oldKey)) {
          const entry = entries.get(oldKey);
          if (entry) {
            if (entry.type === 'script' && entry.worker) {
              console.log(
                `[TextureScripts] Stopping JS worker for "${oldKey}"`,
              );
              entry.worker.postMessage({ type: 'STOP' });
              entry.worker.terminate();
            } else {
              console.log(`[TextureScripts] Disposing SVG texture "${oldKey}"`);
            }
            entry.texture.dispose();
            entries.delete(oldKey);
          }
        }
      }
    }

    // Broadcast after removals (some animated workers may have been removed)
    broadcastAnimationState();
    broadcastTextureWorkerStats();

    // Update uuid → entryKeys mapping
    uuidToEntryKeys.clear();
    for (const [uuid, entryKeys] of neededUuids) {
      uuidToEntryKeys.set(uuid, new Set(entryKeys));
    }

    // Start or restart entries for all needed textures
    for (const [
      entryKey,
      {
        filename,
        channel,
        content,
        scriptCode,
        type: entryType,
        fps,
        width,
        height,
        props: entryProps,
      },
    ] of neededEntries) {
      const existing = entries.get(entryKey);

      if (existing) {
        // Update ref count
        let refCount = 0;
        for (const entryKeys of neededUuids.values()) {
          if (entryKeys.has(entryKey)) refCount++;
        }
        existing.refCount = refCount;

        // Hot-reload: if the source content changed, tear down and recreate
        if (existing.scriptCode === scriptCode) {
          // Re-register UUIDs in the texture map in case geometries changed
          for (const [uuid, entryKeys] of neededUuids) {
            if (entryKeys.has(entryKey)) {
              let channelMap = textureMap.get(uuid);
              if (!channelMap) {
                channelMap = new Map();
                textureMap.set(uuid, channelMap);
              }
              if (!channelMap.has(channel)) {
                channelMap.set(channel, existing.texture);
              }
            }
          }
          continue;
        }

        // Content changed — tear down old entry and fall through to create new one
        console.log(
          `[TextureScripts] Content changed for "${entryKey}", recreating`,
        );
        if (existing.type === 'script' && existing.worker) {
          existing.worker.postMessage({ type: 'STOP' });
          existing.worker.terminate();
        }
        existing.texture.dispose();
        entries.delete(entryKey);
        // Clean up texture map entries for this entryKey
        for (const [uuid, entryKeys] of uuidToEntryKeys) {
          if (entryKeys.has(entryKey)) {
            textureMap.get(uuid)?.delete(channel);
          }
        }
      }

      if (entryType === 'image') {
        spawnStaticImage(
          entryKey,
          filename,
          channel,
          content,
          width,
          height,
          neededUuids,
        );
      } else {
        spawnWorker(
          entryKey,
          filename,
          channel,
          scriptCode,
          fps,
          neededUuids,
          entryProps,
        );
      }
    }
  }, [
    coloredGeometries,
    projectFiles,
    spawnWorker,
    spawnStaticImage,
    broadcastAnimationState,
    broadcastTextureWorkerStats,
  ]);

  // Push updated parameter values to all running JS-script workers whenever
  // the parameters array changes.  This is a lightweight message — no worker
  // restart — so slider drags and live edits update the texture instantly.
  // For static-mode workers (fps=0), the PARAMS message in the worker also
  // re-enables one frame so the texture redraws with the new values.
  // SVG textures do not receive PARAMS — they are fully static.
  useEffect(() => {
    const entries = entriesRef.current;
    if (entries.size === 0) return;
    const params = parametersToRecord(parameters);
    for (const entry of entries.values()) {
      if (entry.type === 'script' && entry.worker) {
        entry.worker.postMessage({ type: 'PARAMS', params });
      }
    }
  }, [parameters]);

  // Cleanup all workers and textures on unmount
  useEffect(() => {
    return () => {
      console.log('[TextureScripts] Unmounting, stopping all workers');
      for (const entry of entriesRef.current.values()) {
        if (entry.type === 'script' && entry.worker) {
          entry.worker.postMessage({ type: 'STOP' });
          entry.worker.terminate();
        }
        entry.texture.dispose();
      }
      entriesRef.current.clear();
      uuidToEntryKeysRef.current.clear();
      sharedStateRef.current.clear();
      textureMapRef.current.clear();
      onHasAnimatedScriptsRef.current?.(false);
      setTextureWorkerStatsRef.current({ active: 0, total: 0 });
    };
  }, []);

  /**
   * Publish a named value to all running texture-script workers.
   * Also updates the sharedState cache so workers that start later receive it.
   * Called from ThreeScene when an interactive() or interactive_publish() region
   * is clicked — allows geometry interactions to drive texture scripts directly
   * without a SCAD recompile.
   * Also notifies external subscribers (e.g. ParameterSection @pubsub params).
   */
  const publish = useCallback((key: string, value: unknown) => {
    sharedStateRef.current.set(key, value);
    for (const entry of entriesRef.current.values()) {
      if (entry.type === 'script' && entry.worker) {
        entry.worker.postMessage({ type: 'DATA', key, value });
      }
    }
    // Also fan out to extra workers (e.g. shader workers)
    if (getExtraWorkers) {
      for (const w of getExtraWorkers()) {
        w.postMessage({ type: 'DATA', key, value });
      }
    }
    // Notify external subscribers (e.g. ParameterSection @pubsub params)
    const subs = subscribersRef.current.get(key);
    if (subs) {
      for (const cb of subs) cb(value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Subscribe to pub/sub messages for a specific key.
   * Returns an unsubscribe function that removes the subscription.
   * The callback will be called whenever a value is published under `key`
   * (from any script worker or from the external publish() call).
   */
  const subscribe = useCallback(
    (key: string, callback: (value: unknown) => void): (() => void) => {
      if (!subscribersRef.current.has(key)) {
        subscribersRef.current.set(key, new Set());
      }
      subscribersRef.current.get(key)!.add(callback);

      // Immediately fire with the last known value if one exists
      if (sharedStateRef.current.has(key)) {
        callback(sharedStateRef.current.get(key));
      }

      return () => {
        const subs = subscribersRef.current.get(key);
        if (subs) {
          subs.delete(callback);
          if (subs.size === 0) {
            subscribersRef.current.delete(key);
          }
        }
      };
    },
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  return { publish, subscribe, sharedStateRef };
}
